import { isArray } from '@dimjs/lang';
import { extend } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { ReactNode, useMemo } from 'react';
import { useArrayChange, useEffectCustom } from '../hooks';
import { CommonPropsWithChildren, withNativeProps } from '../utils/native-props';
import { CheckListContext, CheckListItemValue, CheckListValue } from './context';
import { getValueList } from './utils';

// export type CheckListSelectedValue<T extends 'multi' | 'radio'> = T extends 'multi'
//   ? { value: CheckListItemValue; label: string }[]
//   : CheckListItemValue;

export type CheckListSelectedValue<T extends 'multi' | 'radio'> =
  | (T extends 'multi' ? CheckListItemValue[] : CheckListItemValue)
  | CheckListItemValue
  | CheckListItemValue[];

export type CheckListProps = {
  multiple?: boolean;
  onChange?: (value: CheckListSelectedValue<never>, operateValue: CheckListItemValue) => void;
  onPreChange?: (value: CheckListItemValue) => Promise<void>;
  value?: CheckListValue;
  defaultValue?: CheckListValue;
  beforeExtra?: ReactNode;
  afterExtra?: ReactNode;
  checkedClassPrefix: string;
  stopPropagation?: boolean;
  required?: boolean;
} & CommonPropsWithChildren;

export const CheckList = (props: CheckListProps) => {
  const [checkedValues, arrayOperate] = useArrayChange<CheckListItemValue>([]);

  const valueList = useMemo(() => {
    const value = isArray(props.value) ? extend([], props.value) : props.value;
    return getValueList(value, props.multiple);
  }, [props.value, props.multiple]);

  useEffectCustom(() => {
    const defaultValue = isArray(props.defaultValue) ? extend([], props.defaultValue) : props.defaultValue;
    const defaultValueList = getValueList(defaultValue, props.multiple);
    const current = valueList || defaultValueList;
    arrayOperate.resetList(current || []);
  }, []);

  hooks.useUpdateEffect(() => {
    arrayOperate.resetList(valueList || []);
  }, [valueList]);

  const onChange = hooks.useCallbackRef(async (value: CheckListItemValue, defaultChange) => {
    try {
      if (props.onPreChange) {
        await props.onPreChange(value);
      }
      const targetIndex = checkedValues.findIndex((temp) => temp === value);
      if (props.multiple === true) {
        if (checkedValues.length === 1 && targetIndex >= 0 && props.required) {
          return;
        }
        if (targetIndex >= 0) {
          arrayOperate.delete(targetIndex);
        } else {
          arrayOperate.add(value);
        }
      } else {
        if (targetIndex >= 0 && props.required) {
          return;
        } else {
          arrayOperate.resetList(targetIndex >= 0 ? [] : [value]);
        }
      }
      if (!defaultChange) {
        const newValues = arrayOperate.getList();
        let selectedValue;
        if (props.multiple) {
          selectedValue = newValues.length > 0 ? extend([], newValues) : undefined;
        } else {
          selectedValue = newValues.length > 0 ? newValues[0] : undefined;
        }
        props.onChange?.(selectedValue as CheckListSelectedValue<never>, value);
      }
    } catch (error) {}
  });
  return (
    <CheckListContext.Provider
      value={{
        onChange,
        checkedValues,
        checkedClassPrefix: props.checkedClassPrefix,
        stopPropagation: props.stopPropagation,
      }}
    >
      {props.beforeExtra}
      {withNativeProps(props, <div>{props.children}</div>)}
      {props.afterExtra}
    </CheckListContext.Provider>
  );
};
